import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box, CircularProgress, FormControl, Grid, InputLabel, MenuItem, Select, Typography, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import dayjs from "dayjs";

import { TextItem } from "../../components/TextItem";
import GroupAirQualityMap, { useGroupAirQualityAggregation } from "../../components/environment/GroupAirQualityMap";
import TimeField from "../../components/environment/TimeField";
import { useGetPublicGroupAirQualityDays, useGetPublicGroupAirQualitySession } from "services/ContentOpenManager";
import { CustomHoverCard } from "./OpenDataImpact";
import { InfoRedAlertBox } from "./OpenDataPage";

const DEFAULT_FROM = "00:00";
const DEFAULT_TO = "23:59";

/**
 * "Qualità dell'aria di gruppo" nel portale Open Data: la vista della dashboard in forma anonima.
 * Si sceglie un giorno fra tutti quelli con dati (il periodo selezionato nella pagina non conta) e
 * una finestra oraria; nessun partecipante, solo conteggi, percorso di gruppo e campioni (i campioni
 * presi in sosta non arrivano proprio dal backend).
 */
/** Messaggio da mostrare al posto della mappa, dal più generale al più specifico; null se non ce n'è. */
function windowMessage(t, { failed, daysStatus, days, data, availableMetrics }) {
    if (failed && failed.response?.status === 429)
        return t("openDataGroupAirQualityTooManyRequests", "Troppe richieste in poco tempo: riprova fra un minuto.");
    if (failed) return t("genericError", "Errore nel caricamento dei dati.");
    if (daysStatus === "success" && days.length === 0)
        return t("groupAirQualityNoDays", "Nessuna sessione con dati ambientali in questa iniziativa.");
    if (!data) return null;
    if (data.sessions === 0)
        return t("groupAirQualityNoSessionsInWindow", "Nessuna sessione con dati ambientali nella finestra oraria scelta.");
    if (data.includedSessions === 0 || !(data.pieces && data.pieces.length > 0))
        return t("groupAirQualityNoTrack", "Nessuna sessione della finestra ha insieme dati ambientali e un tracciato GPS: non c'è un percorso su cui disegnare.");
    if (availableMetrics.length === 0)
        return t("groupAirQualityNoValidSamples", "Le sessioni della finestra non hanno campioni validi per nessuna grandezza.");
    return null;
}

export function OpenDataGroupAirQuality() {
    const { id } = useParams();
    const { t } = useTranslation();
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up("md"));

    const [day, setDay] = useState("");
    const [from, setFrom] = useState(DEFAULT_FROM);
    const [to, setTo] = useState(DEFAULT_TO);

    const { days, status: daysStatus, error: daysError } = useGetPublicGroupAirQualityDays(id);

    // Si parte dal giorno più recente con dati.
    useEffect(() => {
        if (!day && days.length > 0) setDay(days[0].day);
    }, [day, days]);

    const { data, status, error } = useGetPublicGroupAirQualitySession(id, day, from, to);
    const aggregation = useGroupAirQualityAggregation(data);
    const { availableMetrics, metricLabel } = aggregation;

    const zone = data?.zone;
    const fmtTime = ts => (ts == null ? "" : dayjs.tz(new Date(ts), zone).format("HH:mm"));
    const fmtDateTime = ts => (ts == null ? "-" : dayjs.tz(new Date(ts), zone).format("HH:mm DD/MM/YYYY"));

    const getTooltipText = dot => aggregation.tooltipText(dot, fmtTime(dot.timestamp));

    const result = aggregation.result;
    const validSamples = result ? result.samples.filter(s => !s.rejected).length : 0;
    const rejectedSamples = result ? result.samples.filter(s => s.rejected).length : 0;

    const message = windowMessage(t, { failed: daysError || error, daysStatus, days, data, availableMetrics });

    const loading = daysStatus === "loading" || (day && status === "loading");

    return (
        <Grid container display={"flex"} justifySelf={"center"} xs={12} width={isDesktop ? "75vw" : "100%"}>
            <CustomHoverCard sx={{ width: "100%" }}>
                <Box sx={{ p: 2 }}>
                    <Grid container justifyContent="center" sx={{ my: 1.5 }}>
                        <Typography variant="h4">{t("groupAirQuality", "Qualità dell'aria di gruppo")}</Typography>
                    </Grid>

                    {days.length > 0 && (
                        <Grid container spacing={2} justifyContent="center" alignItems="center" sx={{ mb: 2 }}>
                            <Grid item>
                                <FormControl size="small" sx={{ minWidth: 220 }}>
                                    <InputLabel id="open-data-group-air-quality-day">{t("groupAirQualityDay", "Giorno")}</InputLabel>
                                    <Select
                                        labelId="open-data-group-air-quality-day"
                                        label={t("groupAirQualityDay", "Giorno")}
                                        value={days.some(d => d.day === day) ? day : ""}
                                        onChange={e => {
                                            setDay(e.target.value);
                                            setFrom(DEFAULT_FROM);
                                            setTo(DEFAULT_TO);
                                        }}
                                    >
                                        {days.map(d => (
                                            <MenuItem key={d.day} value={d.day}>
                                                {dayjs(d.day).format("DD/MM/YYYY")} ({d.sessions})
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item>
                                <TimeField label={t("groupAirQualityFrom", "Dalle")} value={from} onApply={setFrom} />
                            </Grid>
                            <Grid item>
                                <TimeField label={t("groupAirQualityTo", "Alle")} value={to} onApply={setTo} />
                            </Grid>
                        </Grid>
                    )}

                    {data && (
                        <Grid container alignItems="flex-end" sx={{ mb: 2 }}>
                            <TextItem
                                value={`${dayjs(data.day).format("DD/MM/YYYY")} ${data.from}-${data.to}`}
                                label={t("groupAirQualityDay", "Giorno")}
                                md={3}
                            />
                            <TextItem
                                value={`${data.includedSessions} / ${data.sessions}`}
                                label={t("openDataGroupAirQualitySessions", "Sessioni incluse / nella finestra")}
                                md={3}
                            />
                            <TextItem value={fmtDateTime(data.firstStart)} label={t("groupAirQualityFirstStart", "Prima partenza")} md={2} />
                            <TextItem value={fmtDateTime(data.lastEnd)} label={t("groupAirQualityLastEnd", "Ultimo arrivo")} md={2} />
                            <TextItem
                                value={`${validSamples} / ${rejectedSamples}`}
                                label={`${t("openDataGroupAirQualitySamples", "Campioni validi / scartati")} (${metricLabel})`}
                                md={2}
                            />
                        </Grid>
                    )}

                    {loading && (
                        <Box display="flex" justifyContent="center" my={4}>
                            <CircularProgress />
                        </Box>
                    )}

                    {message && !loading && <InfoRedAlertBox textToShow={message} />}

                    {data && !message && (
                        <GroupAirQualityMap
                            pieces={data.pieces}
                            start={data.start}
                            end={data.end}
                            aggregation={aggregation}
                            getTooltipText={getTooltipText}
                        />
                    )}
                </Box>
            </CustomHoverCard>
        </Grid>
    );
}
